'use client';
import { useMemo } from 'react';
import { mentionKey, mentionTokens, splitMentions, type MentionPerson, type MentionToken, type StructuredMention } from 'elowen-plugin-shared/composerMentions';
import { Avatar } from './Avatar';

export interface MentionChipProps {
  /** The token to draw, from `mentionTokens` over a message's structured mentions. */
  token: MentionToken;
  /** The signed-in account id, whose chip is drawn a touch stronger. */
  viewerId?: number;
  /** The word a screen reader hears before the name ("Mention:"), already localized by the caller. */
  prefix?: string;
  /** The mark drawn for an agent mention; the caller owns which product identity that is. */
  agentSrc?: string;
}

/** A stored message's mention, inline in its text: the person's (or the agent's) 16 px `Avatar` (decorative)
 *  and the name as the message wrote it, on a soft pill (`.mention-chip` in primitives.css), a touch stronger
 *  when it names the viewer. Its medium ink is the pill's own rule, taken from the `text-ui` role's weight, so
 *  the component states no typography of its own. Screen readers hear the caller's prefix and then the name;
 *  a copied selection keeps only the name. The avatar and the name's first word never wrap apart (a line may always break after
 *  an inline image, whatever character follows it, so only `nowrap` holds them together); a longer name still
 *  wraps between its words. */
export function MentionChip({ token, viewerId, prefix, agentSrc }: MentionChipProps) {
  const { label } = token;
  const cut = label.search(/\s/);
  const viewer = token.kind === 'person' && token.person.id === viewerId;
  return (
    <span data-testid="mention-chip" data-mention-id={mentionKey(token)} data-viewer={viewer ? '' : undefined} className="mention-chip">
      {prefix ? <span className="sr-only select-none">{prefix} </span> : null}
      <span className="whitespace-nowrap">
        <span aria-hidden className="mention-chip__avatar select-none">
          {token.kind === 'agent'
            ? <Avatar user={{ id: 0, username: label }} src={agentSrc} size={16} />
            : <Avatar user={{ id: token.person.id, username: token.person.username, name: token.person.name, avatar: token.person.avatar ?? undefined }} size={16} />}
        </span>
        {cut < 0 ? label : label.slice(0, cut)}
      </span>
      {cut < 0 ? null : label.slice(cut)}
    </span>
  );
}

/** The tokens of one stored message's structured mentions, kept while the message and the members do not change. */
export function useMentionTokens(mentions: readonly StructuredMention[] | undefined, people: ReadonlyMap<number, MentionPerson> | undefined): MentionToken[] {
  return useMemo(() => mentionTokens(mentions, people), [mentions, people]);
}

export interface MentionTextProps {
  /** The message's plain text, as written. */
  text: string;
  /** The message's structured mentions; absent, no `@` is a mention. */
  mentions?: readonly StructuredMention[];
  /** The people by account id: the chips' current names and avatars. */
  people?: ReadonlyMap<number, MentionPerson>;
  /** The signed-in account, whose chip is drawn a touch stronger. */
  viewerId?: number;
  /** The localized word a screen reader hears before a name (see {@link MentionChipProps.prefix}). */
  prefix?: string;
  /** The mark drawn for an agent mention (see {@link MentionChipProps.agentSrc}). */
  agentSrc?: string;
}

/** A person's plain message text with its mentions drawn as chips. */
export function MentionText({ text, mentions, people, viewerId, prefix, agentSrc }: MentionTextProps) {
  const tokens = useMentionTokens(mentions, people);
  if (!tokens.length) return text;
  return splitMentions(text, tokens).map((part, i) => (typeof part === 'string' ? part : <MentionChip key={i} token={part} viewerId={viewerId} prefix={prefix} agentSrc={agentSrc} />));
}
